import { z } from "zod";

/**
 * The Discord recording bot (0.21.0): it records a team's voice channel, one track
 * per player who linked their Discord account and agreed to be recorded, and keeps
 * the recording for the team; a map's Voice panel adds it in one click.
 */

/** How long a link the bot hands out stays good. */
export const DISCORD_CODE_MINUTES = 15;
/** A recording longer than this is stopped by the bot. */
export const MAX_RECORDING_HOURS = 4;
/** How far from a match's date a recording is offered for its maps. */
export const RECORDING_WINDOW_HOURS = 12;

export interface DiscordAccount {
  discordId: string;
  discordName: string;
  /** Teammates' recordings include my voice. */
  consent: boolean;
  linkedAt: string;
}

/** What the link page shows before linking. */
export interface DiscordCodeInfo {
  kind: "user" | "guild";
  discordName: string;
  guildName: string | null;
  expiresAt: string;
}

export const DiscordLinkInput = z.object({ code: z.string().min(8).max(64), consent: z.boolean() });
export type DiscordLinkInput = z.infer<typeof DiscordLinkInput>;
export const DiscordConsentInput = z.object({ consent: z.boolean() });
export const DiscordTeamLinkInput = z.object({ code: z.string().min(8).max(64), teamId: z.string().uuid() });

/** A team's Discord: its server, and each member's link and consent. */
export interface TeamDiscord {
  guild: { id: string; name: string; linkedAt: string } | null;
  members: { userId: string; name: string; avatarUrl: string | null; discordName: string | null; consent: boolean }[];
  canManage: boolean;
  /** The bot runs on this server (its key is set). */
  botReady: boolean;
  /** Where to add the bot to a Discord server, when the server knows the bot's application. */
  inviteUrl: string | null;
}

/** View channels, send messages, connect: all the bot needs. */
export const DISCORD_BOT_PERMISSIONS = 1024 + 2048 + 1048576;
export const discordInviteUrl = (clientId: string) => `https://discord.com/oauth2/authorize?client_id=${clientId}&scope=bot%20applications.commands&permissions=${DISCORD_BOT_PERMISSIONS}`;

export interface VoiceSessionTrackInfo {
  id: string;
  name: string;
  userId: string | null;
  startedAt: string;
  durationSeconds: number;
  bytes: number;
}

export interface VoiceSessionInfo {
  id: string;
  channelName: string;
  startedAt: string;
  endedAt: string | null;
  startedBy: string | null;
  tracks: VoiceSessionTrackInfo[];
  canDelete: boolean;
}

/** A recording offered for a map: with how many of its tracks are already on it. */
export interface MapVoiceSession extends VoiceSessionInfo {
  added: number;
}

export const AddSessionInput = z.object({ sessionId: z.string().uuid() });

// ---------------------------------------------------------------- the bot's side

export const BotCodeInput = z.object({
  discordId: z.string().regex(/^\d{5,25}$/),
  discordName: z.string().trim().min(1).max(100),
  guildId: z
    .string()
    .regex(/^\d{5,25}$/)
    .optional(),
  guildName: z.string().trim().min(1).max(100).optional(),
});

export interface BotGuild {
  team: { id: string; name: string } | null;
  /** Discord IDs of the team's members who agreed to be recorded. */
  recordable: string[];
  /** Discord IDs of the team's members who linked but did not agree. */
  linkedOnly: string[];
}

export const BotSessionInput = z.object({
  guildId: z.string().regex(/^\d{5,25}$/),
  channelName: z.string().trim().min(1).max(100),
  startedBy: z.string().regex(/^\d{5,25}$/),
  startedAt: z.string().datetime(),
});

export const BotSessionEnd = z.object({ endedAt: z.string().datetime() });

export const BotTrackQuery = z.object({
  discordId: z.string().regex(/^\d{5,25}$/),
  name: z.string().trim().min(1).max(100),
  startedAt: z.string().datetime(),
  durationSeconds: z.coerce
    .number()
    .min(0)
    .max(MAX_RECORDING_HOURS * 3600 + 600),
});

/**
 * Where a recording's track goes on a map, as a guess: the match's date is taken as
 * the demo's start. A guess only (a FACEIT date is when the match was made, a little
 * before the first round): the Voice panel lines it up exactly.
 */
export function estimateOffset(trackStartedAt: string, matchPlayedAt: string | null): number {
  if (!matchPlayedAt) return 0;
  const seconds = (Date.parse(matchPlayedAt) - Date.parse(trackStartedAt)) / 1000;
  return Number.isFinite(seconds) ? Math.round(seconds * 10) / 10 : 0;
}
